import { useEffect, useRef } from "react";
import {
  createInkPointProcessor,
  getCoalescedPointerSamples,
  getPointerType,
  getTouchContactMajor,
  percentile,
  sanitizeInkSettings
} from "../lib/inkDeviceProfile";
import { createInkPointerOwnership } from "../lib/inkPointerOwnership";

function getNativePointerEvent(event) {
  return event?.nativeEvent || event;
}

function createMetrics() {
  const makeZone = () => ({ sampleCount: 0, strokeCount: 0, cancelCount: 0, gaps: [], lastSampleAt: null });
  return {
    pointerTypes: new Set(),
    pressures: [],
    contacts: [],
    gaps: [],
    renderDelays: [],
    trackingLags: [],
    tilts: [],
    twists: [],
    tangentialPressures: [],
    sampleCount: 0,
    coalescedSampleCount: 0,
    predictedEventCount: 0,
    hoverSampleCount: 0,
    strokeCount: 0,
    protectedStrokeCount: 0,
    rejectedContactCount: 0,
    ignoredContactCounts: {},
    pointerCancelCount: 0,
    ownerCancelCount: 0,
    arbitratedPalmCount: 0,
    palmReportedFirstCount: 0,
    ownerTransferCount: 0,
    maxConcurrentContactCount: 0,
    distinctPointerIds: new Set(),
    browserLimited: false,
    handSeparations: [],
    simultaneousContactSampleCount: 0,
    pointerSampleCounts: {},
    strokePointerCounts: {},
    verticalZones: { top: makeZone(), middle: makeZone(), bottom: makeZone() },
    lastSampleAt: null
  };
}

function getVerticalZoneName(point, height) {
  if (!point || !(height > 0)) return null;
  const ratio = point.y / height;
  if (ratio < 1 / 3) return "top";
  if (ratio < 2 / 3) return "middle";
  return "bottom";
}

function getExtents(values) {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  (Array.isArray(values) ? values : []).forEach((rawValue) => {
    const value = Number(rawValue);
    if (!Number.isFinite(value)) return;
    min = Math.min(min, value);
    max = Math.max(max, value);
  });
  return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : { min: 0, max: 0 };
}

function getRange(values) {
  const { min, max } = getExtents(values);
  return Math.max(0, max - min);
}

function classifyInput(metrics, pointerTypes, pressureRange) {
  const penStrokeCount = Number(metrics.strokePointerCounts?.pen || 0);
  const touchStrokeCount = Number(metrics.strokePointerCounts?.touch || 0);
  if (penStrokeCount > 0) {
    const hasRichPenSignal = pressureRange > 0.02
      || metrics.hoverSampleCount > 0
      || getRange(metrics.tilts) > 1
      || getRange(metrics.twists) > 1;
    return hasRichPenSignal ? "Digital pen with pressure" : "Digital pen signal";
  }
  if (touchStrokeCount > 0 && pointerTypes.includes("pen")) {
    return "Pen hover with touch writing contact";
  }
  if (touchStrokeCount > 0 || pointerTypes.includes("touch")) {
    return "Browser touch signal";
  }
  if (pointerTypes.includes("mouse")) return "Mouse or trackpad signal";
  if (pointerTypes.includes("pen")) return "Pen hover detected; write to confirm contact";
  return "Waiting for input";
}

function summarizeMetrics(metrics) {
  const pressures = metrics.pressures.filter((value) => value > 0);
  const contacts = metrics.contacts.filter((value) => value > 0);
  const gaps = metrics.gaps.filter((value) => value >= 0 && value < 1000);
  const renderDelays = metrics.renderDelays.filter((value) => value >= 0 && value < 1000);
  const trackingLags = metrics.trackingLags.filter((value) => value >= 0 && value < 1000);
  const pressureExtents = getExtents(pressures);
  const contactExtents = getExtents(contacts);
  const gapExtents = getExtents(gaps);
  const pressureMin = pressureExtents.min;
  const pressureMax = pressureExtents.max;
  const pointerTypes = [...metrics.pointerTypes];
  const medianGapMs = percentile(gaps, 50);
  const pressureRange = Math.max(0, pressureMax - pressureMin);
  const handSeparations = metrics.handSeparations
    .filter((value) => Number.isFinite(value) && value > 0);

  return {
    pointerTypes,
    inputClassification: classifyInput(metrics, pointerTypes, pressureRange),
    pointerSampleCounts: { ...metrics.pointerSampleCounts },
    strokePointerCounts: { ...metrics.strokePointerCounts },
    sampleCount: metrics.sampleCount,
    coalescedSampleCount: metrics.coalescedSampleCount,
    predictedEventCount: metrics.predictedEventCount,
    hoverSampleCount: metrics.hoverSampleCount,
    strokeCount: metrics.strokeCount,
    protectedStrokeCount: metrics.protectedStrokeCount,
    rejectedContactCount: metrics.rejectedContactCount,
    ignoredContactCounts: { ...metrics.ignoredContactCounts },
    pointerCancelCount: metrics.pointerCancelCount,
    ownerCancelCount: metrics.ownerCancelCount,
    arbitratedPalmCount: metrics.arbitratedPalmCount,
    palmReportedFirstCount: metrics.palmReportedFirstCount,
    ownerTransferCount: metrics.ownerTransferCount,
    maxConcurrentContactCount: metrics.maxConcurrentContactCount,
    distinctPointerCount: metrics.distinctPointerIds.size,
    browserLimited: metrics.browserLimited,
    handSeparation: {
      min: percentile(handSeparations, 0),
      p25: percentile(handSeparations, 25),
      median: percentile(handSeparations, 50),
      p90: percentile(handSeparations, 90),
      max: percentile(handSeparations, 100),
      sampleCount: handSeparations.length
    },
    pressure: {
      min: pressureMin,
      max: pressureMax,
      range: pressureRange
    },
    contact: {
      min: contactExtents.min,
      median: percentile(contacts, 50),
      p90: percentile(contacts, 90),
      max: contactExtents.max
    },
    timing: {
      medianGapMs,
      p95GapMs: percentile(gaps, 95),
      maxGapMs: gapExtents.max,
      sampleRateHz: medianGapMs > 0 ? 1000 / medianGapMs : 0
    },
    rendering: {
      medianRenderDelayMs: percentile(renderDelays, 50),
      p95RenderDelayMs: percentile(renderDelays, 95),
      medianTrackingLagPx: percentile(trackingLags, 50),
      p95TrackingLagPx: percentile(trackingLags, 95)
    },
    stylus: {
      classification: classifyInput(metrics, pointerTypes, pressureRange),
      hoverSampleCount: metrics.hoverSampleCount,
      predictedEventCount: metrics.predictedEventCount,
      tiltRange: getRange(metrics.tilts),
      twistRange: getRange(metrics.twists),
      tangentialPressureRange: getRange(metrics.tangentialPressures)
    },
    verticalZones: Object.fromEntries(Object.entries(metrics.verticalZones).map(([name, zone]) => {
      const gaps = zone.gaps.filter((value) => value >= 0 && value < 1000);
      const medianGapMs = percentile(gaps, 50);
      return [name, {
        sampleCount: zone.sampleCount,
        strokeCount: zone.strokeCount,
        cancelCount: zone.cancelCount,
        medianGapMs,
        p95GapMs: percentile(gaps, 95),
        sampleRateHz: medianGapMs > 0 ? 1000 / medianGapMs : 0
      }];
    })),
    contactSamples: contacts.slice(-240)
  };
}

function drawPaper(ctx, width, height) {
  ctx.fillStyle = "#fffdf7";
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = "rgba(61, 113, 210, 0.14)";
  ctx.lineWidth = 1;
  for (let y = 44; y < height; y += 44) {
    ctx.beginPath();
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(width, y + 0.5);
    ctx.stroke();
  }
  ctx.strokeStyle = "rgba(222, 78, 78, 0.18)";
  ctx.beginPath();
  ctx.moveTo(42.5, 0);
  ctx.lineTo(42.5, height);
  ctx.stroke();
}

function drawAlignmentGuide(ctx, width, height) {
  const centerX = Math.round(width / 2) + 0.5;
  const centerY = Math.round(height / 2) + 0.5;
  ctx.save();
  ctx.strokeStyle = "rgba(31, 91, 224, 0.48)";
  ctx.lineWidth = 1.25;
  ctx.setLineDash([9, 7]);
  ctx.beginPath();
  ctx.moveTo(width * 0.14, centerY);
  ctx.lineTo(width * 0.86, centerY);
  ctx.moveTo(centerX, height * 0.18);
  ctx.lineTo(centerX, height * 0.82);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.arc(centerX, centerY, 8, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

function drawSteadyGuide(ctx, width, height) {
  const centerY = height / 2;
  ctx.save();
  ctx.strokeStyle = "rgba(31, 91, 224, 0.28)";
  ctx.lineWidth = 8;
  ctx.lineCap = "round";
  ctx.setLineDash([2, 12]);
  ctx.beginPath();
  ctx.moveTo(width * 0.12, centerY);
  ctx.bezierCurveTo(width * 0.24, centerY - 115, width * 0.34, centerY + 115, width * 0.46, centerY);
  ctx.bezierCurveTo(width * 0.58, centerY - 115, width * 0.68, centerY + 115, width * 0.8, centerY);
  ctx.bezierCurveTo(width * 0.84, centerY - 35, width * 0.88, centerY - 35, width * 0.9, centerY);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = "rgba(31, 91, 224, 0.62)";
  ctx.beginPath();
  ctx.arc(width * 0.12, centerY, 8, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawSpeedGuide(ctx, width, height) {
  ctx.save();
  ctx.strokeStyle = "rgba(31, 91, 224, 0.3)";
  ctx.fillStyle = "rgba(31, 91, 224, 0.62)";
  ctx.lineWidth = 3;
  ctx.lineCap = "round";
  [0.34, 0.5, 0.66].forEach((ratio) => {
    const y = height * ratio;
    const startX = width * 0.14;
    const endX = width * 0.86;
    ctx.beginPath();
    ctx.arc(startX, y, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.setLineDash([10, 10]);
    ctx.moveTo(startX + 15, y);
    ctx.lineTo(endX, y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(endX, y);
    ctx.lineTo(endX - 13, y - 9);
    ctx.moveTo(endX, y);
    ctx.lineTo(endX - 13, y + 9);
    ctx.stroke();
  });
  ctx.restore();
}

function drawChallengeGuide(ctx, width, height, guide) {
  if (guide === "alignment") drawAlignmentGuide(ctx, width, height);
  if (guide === "steady") drawSteadyGuide(ctx, width, height);
  if (guide === "speed") drawSpeedGuide(ctx, width, height);
}

export default function InkCalibrationCanvas({
  settings,
  onMetricsChange,
  clearSignal,
  mode = "write",
  showAlignmentGuide = false,
  guide = "",
  className = ""
}) {
  const hostRef = useRef(null);
  const canvasRef = useRef(null);
  const sizeRef = useRef({ width: 0, height: 0, dpr: 1 });
  const strokesRef = useRef([]);
  const activeRef = useRef(null);
  const contactPointerIdsRef = useRef(new Set());
  const contactSessionActiveRef = useRef(false);
  const metricsRef = useRef(createMetrics());
  const settingsRef = useRef(sanitizeInkSettings(settings));
  const ownershipRef = useRef(createInkPointerOwnership(settingsRef.current));
  const appliedOffsetRef = useRef({
    x: settingsRef.current.nibOffsetX,
    y: settingsRef.current.nibOffsetY
  });
  const drawFrameRef = useRef(null);
  const renderQueuedAtRef = useRef(null);
  const lastHoverPublishAtRef = useRef(0);
  const pendingResizeRef = useRef(false);

  settingsRef.current = sanitizeInkSettings(settings);
  ownershipRef.current.setSettings(settingsRef.current);

  function publishMetrics() {
    onMetricsChange?.(summarizeMetrics(metricsRef.current));
  }

  function redraw() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { width, height, dpr } = sizeRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawPaper(ctx, width, height);
    drawChallengeGuide(ctx, width, height, guide || (showAlignmentGuide ? "alignment" : ""));

    const allStrokes = activeRef.current
      ? [...strokesRef.current, activeRef.current]
      : strokesRef.current;
    allStrokes.forEach((stroke) => {
      const points = stroke.points || [];
      if (!points.length) return;
      ctx.save();
      ctx.strokeStyle = stroke.color || "#174fd6";
      ctx.fillStyle = stroke.color || "#174fd6";
      ctx.globalAlpha = stroke.opacity || 1;
      ctx.lineWidth = stroke.width || settingsRef.current.lineWidth;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      if (points.length === 1) {
        ctx.beginPath();
        ctx.arc(points[0].x, points[0].y, Math.max(0.8, ctx.lineWidth / 2), 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let index = 1; index < points.length; index += 1) {
          ctx.lineTo(points[index].x, points[index].y);
        }
        ctx.stroke();
      }
      ctx.restore();
    });
  }

  function scheduleRedraw(measureDelay = false) {
    if (measureDelay && renderQueuedAtRef.current === null) {
      renderQueuedAtRef.current = performance.now();
    }
    if (drawFrameRef.current) return;
    drawFrameRef.current = window.requestAnimationFrame(() => {
      drawFrameRef.current = null;
      redraw();
      if (renderQueuedAtRef.current !== null) {
        metricsRef.current.renderDelays.push(performance.now() - renderQueuedAtRef.current);
        renderQueuedAtRef.current = null;
      }
    });
  }

  function resizeCanvas() {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    if (!host || !canvas) return;
    if (activeRef.current && sizeRef.current.width > 0 && sizeRef.current.height > 0) {
      pendingResizeRef.current = true;
      return;
    }
    const width = Math.max(280, Math.round(host.clientWidth));
    const height = Math.max(220, Math.round(host.clientHeight));
    const dpr = Math.max(1, Number(window.devicePixelRatio || 1));
    const current = sizeRef.current;
    if (
      current.width === width
      && current.height === height
      && current.dpr === dpr
      && canvas.width === Math.round(width * dpr)
      && canvas.height === Math.round(height * dpr)
    ) {
      pendingResizeRef.current = false;
      return;
    }
    pendingResizeRef.current = false;
    sizeRef.current = { width, height, dpr };
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    redraw();
  }

  useEffect(() => {
    resizeCanvas();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(resizeCanvas) : null;
    if (observer && hostRef.current) observer.observe(hostRef.current);
    window.addEventListener("resize", resizeCanvas);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", resizeCanvas);
      if (drawFrameRef.current) window.cancelAnimationFrame(drawFrameRef.current);
      renderQueuedAtRef.current = null;
      pendingResizeRef.current = false;
    };
    // Canvas initialization is intentionally tied to this mounted surface only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    strokesRef.current = [];
    activeRef.current = null;
    contactPointerIdsRef.current = new Set();
    contactSessionActiveRef.current = false;
    ownershipRef.current.reset();
    metricsRef.current = createMetrics();
    renderQueuedAtRef.current = null;
    lastHoverPublishAtRef.current = 0;
    pendingResizeRef.current = false;
    redraw();
    publishMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSignal]);

  useEffect(() => {
    const nextOffset = {
      x: settingsRef.current.nibOffsetX,
      y: settingsRef.current.nibOffsetY
    };
    const offsetDeltaX = nextOffset.x - appliedOffsetRef.current.x;
    const offsetDeltaY = nextOffset.y - appliedOffsetRef.current.y;
    if (offsetDeltaX || offsetDeltaY) {
      const rect = canvasRef.current?.getBoundingClientRect();
      const scaleX = rect?.width > 0 ? sizeRef.current.width / rect.width : 1;
      const scaleY = rect?.height > 0 ? sizeRef.current.height / rect.height : 1;
      const shiftStroke = (stroke) => {
        if (!Array.isArray(stroke?.points)) return;
        stroke.points.forEach((point) => {
          point.x += offsetDeltaX * scaleX;
          point.y += offsetDeltaY * scaleY;
        });
      };
      strokesRef.current.forEach(shiftStroke);
      if (activeRef.current && !activeRef.current.contactCapture) shiftStroke(activeRef.current);
    }
    appliedOffsetRef.current = nextOffset;
    redraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  function pointFromEvent(event) {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const scaleX = rect.width > 0 ? sizeRef.current.width / rect.width : 1;
    const scaleY = rect.height > 0 ? sizeRef.current.height / rect.height : 1;
    const cssX = event.clientX - rect.left + settingsRef.current.nibOffsetX;
    const cssY = event.clientY - rect.top + settingsRef.current.nibOffsetY;
    return {
      x: Math.max(0, Math.min(sizeRef.current.width, cssX * scaleX)),
      y: Math.max(0, Math.min(sizeRef.current.height, cssY * scaleY))
    };
  }

  function recordSample(event, options = {}) {
    const metrics = metricsRef.current;
    const pointerType = getPointerType(event);
    const timestamp = Number(event?.timeStamp || performance.now());
    const point = pointFromEvent(event);
    const zoneName = getVerticalZoneName(point, sizeRef.current.height);
    const zone = zoneName ? metrics.verticalZones[zoneName] : null;
    metrics.pointerTypes.add(pointerType);
    if (Number.isFinite(Number(event?.pointerId))) {
      metrics.distinctPointerIds.add(Number(event.pointerId));
    }
    metrics.sampleCount += 1;
    metrics.pointerSampleCounts[pointerType] = Number(metrics.pointerSampleCounts[pointerType] || 0) + 1;
    if (options.coalesced) metrics.coalescedSampleCount += 1;
    if (options.hover) metrics.hoverSampleCount += 1;
    if (Number.isFinite(Number(event?.pressure))) metrics.pressures.push(Number(event.pressure));
    if (Number.isFinite(Number(event?.tiltX))) metrics.tilts.push(Number(event.tiltX));
    if (Number.isFinite(Number(event?.tiltY))) metrics.tilts.push(Number(event.tiltY));
    if (Number.isFinite(Number(event?.twist))) metrics.twists.push(Number(event.twist));
    if (Number.isFinite(Number(event?.tangentialPressure))) {
      metrics.tangentialPressures.push(Number(event.tangentialPressure));
    }
    if (typeof event?.getPredictedEvents === "function") {
      try {
        const predicted = Array.from(event.getPredictedEvents() || []);
        metrics.predictedEventCount += predicted.length;
      } catch (err) {
        // Prediction is optional and can disappear during Android pointer hand-off.
      }
    }
    const contact = getTouchContactMajor(event);
    if (contact > 0) metrics.contacts.push(contact);
    if (!options.skipTiming && metrics.lastSampleAt !== null && timestamp >= metrics.lastSampleAt) {
      metrics.gaps.push(timestamp - metrics.lastSampleAt);
    }
    if (!options.skipTiming) metrics.lastSampleAt = timestamp;
    if (zone) {
      zone.sampleCount += 1;
      if (!options.skipTiming && zone.lastSampleAt !== null && timestamp >= zone.lastSampleAt) {
        zone.gaps.push(timestamp - zone.lastSampleAt);
      }
      if (!options.skipTiming) zone.lastSampleAt = timestamp;
    }
  }

  function recordHandSeparation(value) {
    const separation = Number(value);
    if (!(separation > 0) || !Number.isFinite(separation)) return;
    metricsRef.current.handSeparations.push(separation);
    if (metricsRef.current.handSeparations.length > 480) {
      metricsRef.current.handSeparations.splice(0, metricsRef.current.handSeparations.length - 480);
    }
    metricsRef.current.simultaneousContactSampleCount += 1;
  }

  function addPoint(event, options = {}) {
    const active = activeRef.current;
    if (!active) return false;
    const rawPoint = pointFromEvent(event);
    if (!rawPoint) return false;
    const processed = active.processor.process(rawPoint.x, rawPoint.y, event?.timeStamp, options);
    if (!processed) return false;
    const point = { x: processed.x, y: processed.y };
    metricsRef.current.trackingLags.push(processed.trackingLagPx);
    const points = active.points;
    const last = points[points.length - 1];
    if (last) {
      const dx = point.x - last.x;
      const dy = point.y - last.y;
      const distance = Math.hypot(dx, dy);
      const minDistance = settingsRef.current.minPointDistance;
      if (distance < minDistance) return false;
      const segmentLength = settingsRef.current.interpolationSegmentLength;
      if (segmentLength > 0 && distance > segmentLength * 1.5) {
        const segmentCount = Math.min(12, Math.max(0, Math.floor(distance / segmentLength) - 1));
        for (let index = 1; index <= segmentCount; index += 1) {
          const ratio = index / (segmentCount + 1);
          points.push({ x: last.x + dx * ratio, y: last.y + dy * ratio });
        }
      }
    }
    points.push(point);
    return true;
  }

  function beginOwnedStroke(action) {
    const samples = Array.isArray(action?.samples) ? action.samples : [];
    const first = samples[0];
    if (!first) return false;
    const firstPoint = pointFromEvent(first);
    activeRef.current = {
      pointerId: action.pointerId,
      pointerType: action.pointerType || getPointerType(first),
      startedAt: Number(first.timeStamp || performance.now()),
      width: settingsRef.current.lineWidth,
      processor: createInkPointProcessor(settingsRef.current),
      protectedByPalm: Number(action.handSeparationPx || 0) > 0,
      verticalZone: getVerticalZoneName(firstPoint, sizeRef.current.height),
      points: []
    };
    samples.forEach((sample) => addPoint(sample));
    metricsRef.current.arbitratedPalmCount += Number(action.blockedContactCount || 0);
    if (action.palmReportedFirst) metricsRef.current.palmReportedFirstCount += 1;
    if (action.replacedPointerId !== null && action.replacedPointerId !== undefined) {
      metricsRef.current.ownerTransferCount += 1;
    }
    recordHandSeparation(action.handSeparationPx);
    scheduleRedraw(true);
    return true;
  }

  function handlePointerDown(event) {
    event.preventDefault();
    const pointerEvent = getNativePointerEvent(event);
    metricsRef.current.lastSampleAt = null;
    Object.values(metricsRef.current.verticalZones).forEach((zone) => { zone.lastSampleAt = null; });
    recordSample(pointerEvent);
    const contact = getTouchContactMajor(pointerEvent);

    if (mode === "contact") {
      contactPointerIdsRef.current.add(pointerEvent.pointerId);
      contactSessionActiveRef.current = true;
      if (contact > 0) {
        const point = pointFromEvent(pointerEvent);
        if (point) {
          strokesRef.current.push({
            points: [point],
            color: "#e05d35",
            width: Math.max(5, Math.min(24, contact / 2)),
            opacity: 0.45
          });
          scheduleRedraw();
        }
      }
      try {
        event.currentTarget.setPointerCapture(pointerEvent.pointerId);
      } catch (err) {
        // Sustained palm samples still work while the pointer remains over the pad.
      }
      return;
    }

    const action = ownershipRef.current.pointerDown(pointerEvent);
    metricsRef.current.maxConcurrentContactCount = Math.max(
      metricsRef.current.maxConcurrentContactCount,
      Number(ownershipRef.current.getState().activeContactCount || 0)
    );
    if (action.type === "start") {
      if (action.replacedPointerId !== null && action.replacedPointerId !== undefined) {
        activeRef.current = null;
        scheduleRedraw();
      }
      beginOwnedStroke(action);
      try {
        event.currentTarget.setPointerCapture(pointerEvent.pointerId);
      } catch (err) {
        // Pointer capture is an optimization, not a requirement for the diagnostic.
      }
    } else if (action.type === "ignore") {
      metricsRef.current.rejectedContactCount += 1;
      metricsRef.current.ignoredContactCounts[action.reason] =
        Number(metricsRef.current.ignoredContactCounts[action.reason] || 0) + 1;
      publishMetrics();
    }
  }

  function handlePointerMove(event) {
    const pointerEvent = getNativePointerEvent(event);
    if (mode === "contact") {
      if (!contactPointerIdsRef.current.has(pointerEvent.pointerId)) return;
      event.preventDefault();
      recordSample(pointerEvent);
      return;
    }
    const action = ownershipRef.current.pointerMove(pointerEvent);
    if (action.type === "ignore") {
      if (getPointerType(pointerEvent) === "pen" && Number(pointerEvent.pressure || 0) === 0) {
        recordSample(pointerEvent, { hover: true, skipTiming: true });
        const now = performance.now();
        if (now - lastHoverPublishAtRef.current > 180) {
          lastHoverPublishAtRef.current = now;
          publishMetrics();
        }
      }
      return;
    }
    event.preventDefault();
    if (action.type === "start") {
      action.samples.forEach((sample) => recordSample(sample, { skipTiming: true }));
      beginOwnedStroke(action);
      try {
        event.currentTarget.setPointerCapture(pointerEvent.pointerId);
      } catch (err) {
        // The stroke remains valid if this browser cannot capture it.
      }
      return;
    }
    if (action.type !== "move" || !activeRef.current) return;
    if (Number(action.handSeparationPx || 0) > 0) {
      activeRef.current.protectedByPalm = true;
    }
    const samples = getCoalescedPointerSamples(
      pointerEvent,
      settingsRef.current.coalescedLimit
    );
    let changed = false;
    samples.forEach((sample) => {
      recordSample(sample, { coalesced: sample !== pointerEvent });
      if (addPoint(sample)) changed = true;
    });
    recordHandSeparation(action.handSeparationPx);
    if (changed) scheduleRedraw(true);
  }

  function finishPointer(event, options = {}) {
    const pointerEvent = getNativePointerEvent(event);
    if (mode === "contact") {
      if (!contactPointerIdsRef.current.has(pointerEvent.pointerId)) return;
      event.preventDefault();
      recordSample(pointerEvent);
      contactPointerIdsRef.current.delete(pointerEvent.pointerId);
      try {
        event.currentTarget.releasePointerCapture(pointerEvent.pointerId);
      } catch (err) {
        // Browsers may release capture automatically.
      }
      if (contactSessionActiveRef.current && contactPointerIdsRef.current.size === 0) {
        contactSessionActiveRef.current = false;
        metricsRef.current.strokeCount += 1;
        publishMetrics();
      }
      return;
    }
    const action = ownershipRef.current.pointerUp(pointerEvent, options);
    if (options.cancelled) {
      metricsRef.current.pointerCancelCount += 1;
      if (action.browserLimited) {
        metricsRef.current.ownerCancelCount += 1;
        metricsRef.current.browserLimited = true;
      }
    }
    if (action.type === "ignore") {
      if (options.cancelled) publishMetrics();
      return;
    }
    event.preventDefault();
    if (action.type === "tap") {
      beginOwnedStroke(action);
    }
    const active = activeRef.current;
    if (!active || active.pointerId !== action.pointerId) return;
    if (action.type !== "cancel") {
      recordSample(pointerEvent);
      addPoint(pointerEvent, { final: true });
    }
    recordHandSeparation(action.handSeparationPx);
    strokesRef.current.push(active);
    activeRef.current = null;
    metricsRef.current.strokeCount += 1;
    if (active.verticalZone && metricsRef.current.verticalZones[active.verticalZone]) {
      metricsRef.current.verticalZones[active.verticalZone].strokeCount += 1;
      if (action.type === "cancel") {
        metricsRef.current.verticalZones[active.verticalZone].cancelCount += 1;
      }
    }
    if (action.type !== "cancel" && active.protectedByPalm) {
      metricsRef.current.protectedStrokeCount += 1;
    }
    metricsRef.current.strokePointerCounts[active.pointerType] =
      Number(metricsRef.current.strokePointerCounts[active.pointerType] || 0) + 1;
    try {
      event.currentTarget.releasePointerCapture(pointerEvent.pointerId);
    } catch (err) {
      // Browsers may release capture automatically on cancellation.
    }
    scheduleRedraw(true);
    publishMetrics();
    if (pendingResizeRef.current) {
      window.requestAnimationFrame(resizeCanvas);
    }
  }

  return (
    <div ref={hostRef} className={`ink-calibration-canvas ${className}`.trim()}>
      <canvas
        ref={canvasRef}
        aria-label={mode === "contact" ? "Palm contact test area" : "Writing calibration slate"}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishPointer}
        onPointerCancel={(event) => finishPointer(event, { cancelled: true })}
      />
    </div>
  );
}
